/**
 * CURSANDO AHORA (SUJ-T8 bloque 4, orden_P de #17533).
 *
 * Una seccion de la ficha unica con lo que la persona esta estudiando, POR PROGRAMA, en una de dos
 * pieles segun el dato que haya:
 *   - ESCALA: el programa tiene perfil del tutor (learning_profiles). Se pinta la escalera de
 *     niveles del pensum (Ingles: A2 -> B1 -> B2), el peldano actual, el tema en curso ("1 de 8"),
 *     los temas dominados y las sesiones. Es el caso de Josse en PENSUM-INGLES-001.
 *   - QUIZ: no hay perfil, pero respondio el quiz del panel (persona.capacitacion). Se dice cuantas
 *     respondio y cuantas bien. El quiz no guarda de que programa era: la piel lo dice.
 *
 * DE DONDE SALE EL SUJETO: persona_llaves.persona_id ES context_subjects.id (medido el 22-sep: 11
 * de 11). La ficha ya tiene personaId, asi que pide GET learning/profile?subject={personaId}
 * &program={codigo} sin endpoint nuevo. Que programas: los del pensum (GET learning/pensum); el
 * 404 de perfil es "no esta en ese programa", no un error.
 *
 * LO QUE NO SE INVENTA: el nivel meta (la API no publica level_target) y el dominio confirmado.
 * El dominio que se muestra es la ULTIMA estimacion del tutor para el tema en curso, y se dice
 * que esta sin confirmar (la confirma el dueno en Accesos de asistentes).
 *
 * Sin DOM: lo prueba scripts/check-ficha-cursando.mjs.
 */

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());
const lista = (v) => (Array.isArray(v) ? v : []);
const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};

/** El nivel de un peldano sale del codigo de la skill: SKILL-ENG-A2 -> A2. Si no calza, el codigo. */
export function nivelDeLaSkill(code) {
  const c = texto(code);
  const m = c.match(/-([ABC][12])$/i);
  return m ? m[1].toUpperCase() : c.replace(/^SKILL-/i, '');
}

/** level_current llega como objeto ({level:'A2'}) o como texto JSON; se aceptan los dos. */
export function nivelDelPerfil(levelCurrent) {
  let v = levelCurrent;
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return '';
    try { v = JSON.parse(s); } catch { return s; }
  }
  return texto(pick(v, 'level', 'Level', 'nivel')).toUpperCase();
}

/** La skill del peldano en que va (para pedir sus temas); '' si el nivel no calza con ninguna. */
export function skillActual(programa, perfil) {
  const nivel = nivelDelPerfil(perfil?.nivel);
  return lista(programa?.skills).find((code) => nivelDeLaSkill(code) === nivel) || '';
}

/** Los programas del pensum, activos, con su escalera de skills. */
export function programasDelPensum(resPensum) {
  const d = resPensum?.data?.data ?? resPensum?.data ?? [];
  return lista(d)
    .filter((p) => pick(p, 'is_active', 'isActive') !== false)
    .map((p) => ({
      codigo: texto(pick(p, 'code', 'Code')),
      nombre: texto(pick(p, 'name', 'Name')),
      skills: lista(pick(p, 'skill_codes', 'skillCodes', 'SkillCodes')).map(texto).filter(Boolean)
    }))
    .filter((p) => p.codigo);
}

/** La ultima estimacion del tutor (topic_mastery) para un tema, en %; null si no hay. */
export function dominioEstimado(pendientes, tema) {
  const t = texto(tema);
  if (!t) return null;
  const del = lista(pendientes)
    .filter((o) => texto(o.metrica) === 'topic_mastery' && texto(o.tema) === t)
    .sort((a, b) => (Date.parse(b.cuando) || 0) - (Date.parse(a.cuando) || 0));
  for (const o of del) {
    let v = o.valor;
    try { v = typeof v === 'string' ? JSON.parse(v) : v; } catch { /* texto suelto */ }
    const s = Number(typeof v === 'object' && v ? pick(v, 'score', 'Score') : v);
    if (Number.isFinite(s)) return Math.round(s <= 1 ? s * 100 : s);
  }
  return null;
}

/**
 * La piel ESCALA de un programa con perfil.
 * @param programa  {codigo, nombre, skills}
 * @param r         respuesta normalizada de LearningProfileApiClient.getProfile (ok:true)
 * @param temas     los temas del peldano actual, en orden (codigos)
 */
export function escalaDelPrograma(programa, r, temas = []) {
  const perfil = r?.perfil || {};
  const nivel = nivelDelPerfil(perfil.nivel);
  const niveles = programa.skills.map(nivelDeLaSkill);
  const idx = niveles.indexOf(nivel);
  const peldanos = programa.skills.map((code, i) => ({
    code,
    nivel: niveles[i],
    estado: idx < 0 ? 'por_venir' : i < idx ? 'hecho' : i === idx ? 'actual' : 'por_venir'
  }));
  const tema = texto(perfil.tema);
  const cods = lista(temas).map(texto).filter(Boolean);
  const pos = cods.indexOf(tema);
  const dominados = lista(perfil.dominados).filter((t) => !cods.length || cods.includes(t)).length;
  const sesiones = lista(r?.sesiones);
  const ultima = sesiones.map((s) => s.desde).filter(Boolean).sort().pop() || '';
  return {
    piel: 'escala',
    programa: programa.codigo,
    nombre: programa.nombre || programa.codigo,
    nivel: nivel || '',
    nivelConocido: idx >= 0,
    peldanos,
    tema,
    temaN: pos >= 0 ? pos + 1 : null,
    temasTotal: cods.length,
    dominados,
    dominioEstimado: dominioEstimado(r?.pendientes, tema),
    sesiones: sesiones.length,
    ultimaSesion: ultima,
    validado: Boolean(texto(perfil.validadoPor))
  };
}

/** La piel QUIZ, de persona.capacitacion. null si nunca respondio. */
export function quizDeLaPersona(capacitacion) {
  const resp = Math.trunc(Number(capacitacion?.respuestas) || 0);
  if (resp <= 0) return null;
  const bien = Math.trunc(Number(capacitacion?.correctas) || 0);
  return {
    piel: 'quiz',
    programa: '',
    nombre: 'Quiz del panel',
    respuestas: resp,
    correctas: bien,
    aciertoPct: Math.round((100 * bien) / resp),
    ultima: texto(capacitacion?.ultima),
    nota: 'El quiz no guarda de qué programa era la pregunta: es el total de todas.'
  };
}

/**
 * Todo lo que va en la seccion, en orden: primero los programas con escala, despues el quiz.
 * @param perfiles  [{programa, r, temas}] solo los que respondieron ok
 */
export function cursandoAhora({ perfiles = [], capacitacion = null } = {}) {
  const items = perfiles.filter((x) => x?.r?.ok && x.r.perfil).map((x) => escalaDelPrograma(x.programa, x.r, x.temas));
  const quiz = quizDeLaPersona(capacitacion);
  if (quiz) items.push(quiz);
  return items;
}

/** Cuando no hay nada que mostrar, por que. */
export function porQueNoCursaNada({ fallos = [], sinPerfil = 0 } = {}) {
  const prohibido = fallos.find((f) => f.motivo === 'PROHIBIDO' || f.motivo === 'SIN_SESION');
  if (prohibido) return { motivo: 'SIN_PERMISO', frase: 'Tu sesión no puede ver el aprendizaje de otra persona: es para dueño o administrador.' };
  const otro = fallos.find((f) => f.motivo !== 'SIN_PERFIL');
  if (otro) return { motivo: otro.motivo || 'ERROR_API', frase: `No pudimos consultar su aprendizaje: ${otro.detalle || 'sin respuesta de la API'}.` };
  return { motivo: 'NADA', frase: `No está en ningún programa (${sinPerfil} revisados) y no ha respondido el quiz del panel.` };
}
